import * as React from "react";
import { Button as Base } from "rebass/styled-components";
import styled, { css } from "styled-components/";

export interface IButtonStyledProps {
  disabled?: boolean;
  fullwidth?: string; // "true"
  variant?: string; //"primary" | "secondary" | "link";
  size?: string; // "sm" || "md" || "lg"
  hasicon: string; // "true" || ""
}

const btnVariations = {
  baseBtn: css`
    display: inline-block;
    padding: 1.2rem;
    background-color: transparent;
    font-size: 1.6rem;
    color: ${({ theme }) => theme.colors.whiteDarker2};
    line-height: 1;
    font-weight: 500;
    font-family: ${({ theme }) => theme.fonts.impactMedium};
    border-radius: 0;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 250ms linear;
    text-decoration: none;
    border-radius: 40px;
  `,
  primary: css`
    background-color: ${({ theme }) => theme.colors.red};

    &:hover {
      color: ${({ theme }) => theme.colors.whiteDarker2};
      background-color: ${({ theme }) => theme.colors.redDarker};
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.blueDarker4};
    color: ${({ theme }) => theme.colors.white};

    &:hover {
      background-color: ${({ theme }) => theme.colors.blueDarker1};
    }
  `,
  link: css`
    background-color: transparent;

    &:hover {
      background-color: transparent;
    }
  `,
  fullwidth: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `,
  xs: css`
    font-size: 1.6rem;
    padding: 0.8rem;
  `,
  sm: css`
    font-size: 24px;
    padding: 1rem;
  `,
  lg: css`
    padding: 1.6rem;
  `,
};

const StyledBtn = styled(Base)<IButtonStyledProps>`
  ${btnVariations.baseBtn};
  ${({ variant }) => variant === "primary" && btnVariations.primary};
  ${({ variant }) => variant === "secondary" && btnVariations.secondary};
  ${({ variant }) => variant === "link" && btnVariations.link};
  ${({ fullwidth }) => fullwidth === "true" && btnVariations.fullwidth}
  ${({ size }) => size === "xs" && btnVariations.xs}
  ${({ size }) => size === "sm" && btnVariations.sm}
  ${({ size }) => size === "lg" && btnVariations.lg}
  ${({ fullwidth, hasicon }) =>
    fullwidth === "true" &&
    hasicon === "true" &&
    css`
      justify-content: space-between;
    `};
  ${({ disabled, theme }) =>
    disabled &&
    css`
      box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.purple4};
      background-color: ${theme.colors.greyDarker2};
      color: ${theme.colors.blackLighter};
      cursor: auto;

      &:hover {
        background-color: ${theme.colors.greyDarker2};
        color: ${theme.colors.blackLighter};
        cursor: not-allowed;
        pointer-events: all !important;
      }
    `}
`;

interface IProps {
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  width?: number;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
  children: React.ReactNode;
  disabled?: boolean;
  variant?: string;
  size?: string;
  // Need to clean this part up
  to?: string;
  href?: string;
  as?: any;
  tabIndex?: number;
  role?: string;
  target?: string;
}

const Button: React.ForwardRefExoticComponent<IProps> = React.forwardRef(
  (
    {
      as,
      width = 0,
      children,
      disabled = false,
      variant = "primary",
      type = "button",
      startIcon: startIconProp,
      endIcon: endIconProp,
      ...props
    }: IProps,
    ref?: React.Ref<HTMLButtonElement>,
  ) => {
    const commonProps = {
      ref,
      disabled,
      variant,
      fullwidth: width === 1 ? "true" : "undefined",
      hasicon: !(startIconProp || endIconProp) ? "" : "true",
    };

    const startIcon = startIconProp && (
      <span style={{ marginRight: 8 }}>{startIconProp}</span>
    );

    const endIcon = endIconProp && (
      <span style={{ marginLeft: 8 }}>{endIconProp}</span>
    );

    // If it has a "href" prop we assume it's wants to be an anchor
    // Or it could be a Link component but you can use the prop.as for this as={Link}
    if (props.href || props.to) {
      if (disabled) {
        props.tabIndex = -1;
        props.role = "button";
      }
      return (
        <StyledBtn as={as || "a"} {...commonProps} {...props}>
          {startIcon}
          {children}
          {endIcon}
        </StyledBtn>
      );
    }

    return (
      <StyledBtn type={type} {...commonProps} {...props}>
        {startIcon}
        {children}
        {endIcon}
      </StyledBtn>
    );
  },
);

export default Button;
